import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import colorSet from "@/src/styles/color";
import zIndex from "@/src/styles/zIndex";
import Icon from "@/src/components/atom/Icon";
import CloseSvg from "@/src/assets/icons/icon-close-black.svg";
import ChatIconUserSvg from "@/src/assets/icons/chat-icon-user.svg";
import { useUploadSmallImageMutation } from "@/src/store/apis/media";
import Loader from "@/src/components/atom/Loader";
import { useTranslation } from "react-i18next";

interface EditChannelInformationModalProps {
  visible: boolean;
  onClose: () => void;
  channel: {
    url: string;
    name: string;
    coverUrl?: string;
  } | null;
  onSave: (updates: {
    channelName: string;
    channelImage?: string;
    channelImageFile?: File;
  }) => Promise<void>;
}

const EditChannelInformationModal: React.FC<
  EditChannelInformationModalProps
> = ({ visible, onClose, channel, onSave }) => {
  const { t } = useTranslation("chat");
  const [channelName, setChannelName] = useState("");
  const [channelImage, setChannelImage] = useState("");
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [tempImagePreview, setTempImagePreview] = useState<string | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadSmallImage] = useUploadSmallImageMutation();

  // SendBird 기본 프로필 URL 체크 함수
  const isDefaultProfileUrl = (url: string) => {
    return (
      !url ||
      url.includes("profile_placeholder.png") ||
      url.includes("sample/cover")
    );
  };

  useEffect(() => {
    if (channel) {
      setChannelName(channel.name || "");

      const currentCoverUrl = channel.coverUrl;
      if (currentCoverUrl && !isDefaultProfileUrl(currentCoverUrl)) {
        setChannelImage(currentCoverUrl);
        setIsDeleted(false);
      } else {
        setChannelImage("");
        setIsDeleted(true);
      }

      setTempImageFile(null);
      setTempImagePreview(null);
    }
  }, [channel]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t("fileSizeTooLarge"));
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert(t("pleaseUploadImage"));
        return;
      }

      setTempImageFile(file);
      setIsDeleted(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    setTempImageFile(null);
    setTempImagePreview(null);
    setIsDeleted(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setIsUploading(true);
    try {
      let fallbackImageUrl: string | undefined;

      // 이미지 파일이 있고 SendBird 업로드가 실패할 경우를 대비해 미리 서버에 업로드
      if (tempImageFile) {
        try {
          const result = await uploadSmallImage({
            files: [tempImageFile],
            folder: "commons/",
          }).unwrap();

          if (result && result.length > 0) {
            fallbackImageUrl = result[0].mediaUrl;
          }
        } catch (serverError) {
          console.error("Failed to prepare fallback image:", serverError);
        }
      }

      // SendBird를 통해 직접 채널 이미지 업데이트
      // 채널 이미지는 파일 객체를 직접 전달
      await onSave({
        channelName,
        channelImage: isDeleted ? "" : fallbackImageUrl, // 삭제 시 빈 문자열, 아니면 폴백 URL
        channelImageFile: tempImageFile || undefined, // 파일 객체 전달
      });
      onClose();
    } catch (error) {
      console.error("Failed to save channel information:", error);
      alert(t("failedToSaveChannelInfo"));
    } finally {
      setIsUploading(false);
    }
  };

  if (!visible || !channel) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t("editChannelInformation")}</ModalTitle>
          <CloseButton onClick={onClose}>
            <Icon iconSrc={CloseSvg} iconSize={22} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ChannelImageSection>
            <SectionLabel>{t("channelImage")}</SectionLabel>
            <ChannelImageContainer>
              <ChannelImageWrapper>
                {tempImagePreview || (!isDeleted && channelImage) ? (
                  <ChannelImage
                    src={tempImagePreview || channelImage}
                    alt="Channel"
                  />
                ) : (
                  <DefaultChannelWrapper>
                    <img
                      src={ChatIconUserSvg}
                      alt="Default"
                      style={{
                        width: "45.714px",
                        height: "45.714px",
                      }}
                    />
                  </DefaultChannelWrapper>
                )}
              </ChannelImageWrapper>
              <ChannelButtonGroup>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                  id="channel-image-upload"
                />
                {tempImagePreview || (!isDeleted && channelImage) ? (
                  <>
                    <ChannelButton as="label" htmlFor="channel-image-upload">
                      {t("edit")}
                    </ChannelButton>
                    <DeleteButton onClick={handleDeleteImage}>
                      {t("delete")}
                    </DeleteButton>
                  </>
                ) : (
                  <ChannelButton as="label" htmlFor="channel-image-upload">
                    {t("upload")}
                  </ChannelButton>
                )}
              </ChannelButtonGroup>
            </ChannelImageContainer>
          </ChannelImageSection>

          <FormSection>
            <FormField>
              <Label>{t("channelName")}</Label>
              <Input
                type="text"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                placeholder={t("enterChannelName")}
              />
            </FormField>
          </FormSection>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>{t("cancel")}</CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={isUploading}
            $isLoading={isUploading}
          >
            {isUploading ? (
              <Loader
                size={16}
                tickColor="white"
                trackColor="rgba(255, 255, 255, 0.3)"
              />
            ) : (
              t("save")
            )}
          </SaveButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditChannelInformationModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${zIndex.floatingChatEditModal};
`;

const ModalContent = styled.div`
  background: ${colorSet.white};
  border-radius: 16px;
  width: 480px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 16px 24px;
  gap: 16px;
`;

const ModalTitle = styled.h2`
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: ${colorSet.gray2};
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f4f6;
  }

  &:active {
    background-color: #e7edf0;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px 8px 24px;
`;

const ChannelImageSection = styled.div`
  margin-bottom: 34px;
`;

const SectionLabel = styled.label`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  color: ${colorSet.gray2};
`;

const ChannelImageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

const ChannelImageWrapper = styled.div`
  position: relative;
`;

const ChannelImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
`;

const DefaultChannelWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #aeb7bc;

  img {
    filter: brightness(0) invert(1);
  }
`;

const ChannelButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ChannelButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border: 1px solid ${colorSet.gray7};
  border-radius: 4px;
  background: white;
  color: ${colorSet.gray2};
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition:
    color 0.2s,
    border-color 0.2s;
  min-width: 74px;
  height: 30px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    color: #166dd7;
    border-color: #166dd7;
  }

  &:active {
    color: #166dd7;
    border-color: #166dd7;
    background-color: #d7e7f9;
  }
`;

const DeleteButton = styled(ChannelButton)`
  color: ${colorSet.red1};
  border-color: ${colorSet.red6};

  &:hover {
    color: ${colorSet.red2};
    border-color: ${colorSet.red4};
  }

  &:active {
    color: ${colorSet.red1};
    border-color: ${colorSet.red1};
    background-color: ${colorSet.red7};
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
`;

const Label = styled.label`
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  color: ${colorSet.gray6};
`;

const Input = styled.input`
  border: 1px solid ${colorSet.gray7};
  outline: none;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colorSet.gray2};
  padding: 8px 12px;
  background: transparent;
  border-radius: 4px;
  transition: border-color 0.2s;

  &:focus {
    border-color: #166dd7;
  }

  &::placeholder {
    color: ${colorSet.gray5};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 34px 32px 24px 32px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid ${colorSet.gray7};
  border-radius: 4px;
  background: white;
  color: ${colorSet.gray2};
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background-color 0.2s;
  min-width: 74px;
  height: 38px;

  &:hover {
    color: #166dd7;
    border-color: #166dd7;
  }

  &:active {
    color: #166dd7;
    border-color: #166dd7;
    background-color: #d7e7f9;
  }
`;

const SaveButton = styled.button<{ disabled?: boolean; $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  background: ${(props) =>
    props.$isLoading ? "#166DD7" : props.disabled ? colorSet.gray4 : "#166DD7"};
  color: white;
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;
  min-width: 74px;
  height: 38px;

  &:hover {
    background: ${(props) =>
      props.$isLoading
        ? "#166DD7"
        : props.disabled
          ? colorSet.gray4
          : "#1761C2"};
  }

  &:active {
    background: ${(props) =>
      props.$isLoading
        ? "#166DD7"
        : props.disabled
          ? colorSet.gray4
          : "#1150AA"};
  }
`;
